import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, IconControl, Loading } from "spark-core-dx/components";
import { useCorePermissions, useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { Operations } from "../../../services/ItemServices";
import "./ResourceApprovalsManage.scss";

const itemServiceFactory = new ItemServiceFactory();

const CLIENT_RESOURCE_STATUS_LABELS = {
  4: "Active",
  6: "Suspended",
  8: "Cancelled",
  9: "Retired",
  10: "Pending Approval"
};

const APPROVAL_STATUS_LABELS = {
  1: "Not Submitted",
  2: "Pending Review",
  3: "Approved",
  4: "Rejected",
  5: "Changes Requested",
  6: "Cancelled"
};

const unwrapResult = (result) => result?.Data ?? result?.data ?? result ?? null;

const getArray = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (Array.isArray(value)) return value;
  }

  return [];
};

const getValue = (item, ...keys) => {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return "";
};

const getString = (item, ...keys) => String(getValue(item, ...keys) ?? "").trim();

const getNumber = (item, ...keys) => {
  const parsed = Number(getValue(item, ...keys));
  return Number.isFinite(parsed) ? parsed : null;
};

const getItems = (result) => {
  const normalized = unwrapResult(result);
  if (Array.isArray(normalized?.Items)) return normalized.Items;
  if (Array.isArray(normalized?.items)) return normalized.items;
  if (normalized?.Item) return [normalized.Item];
  if (normalized?.item) return [normalized.item];
  return [];
};

const getDisplayMessage = (result, fallbackMessage) => {
  const normalized = unwrapResult(result);
  return normalized?.DisplayMessage
    ?? normalized?.Message?.DisplayMessage
    ?? normalized?.Message?.Content
    ?? normalized?.message
    ?? fallbackMessage;
};

const getId = (item) => getString(item, "_id", "Id", "id", "UniqueId", "uniqueId");

const getStatusLabel = (labels, value) => labels[Number(value)] || "Unknown";

const formatDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const formatCapacity = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "Not set";
  if (numeric >= 2147483647) return "Unlimited";
  return numeric.toLocaleString();
};

const formatDisplayLabel = (value) =>
  String(value ?? "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");

const getRequestedUtc = (resource) => {
  const activity = getArray(resource, "Activity", "activity")
    .filter((entry) => ["MarketplaceAdd", "MarketplaceAddPendingApproval", "ProductOfferPurchase"].includes(getString(entry, "ActivityType", "activityType")))
    .sort((left, right) => String(getValue(right, "ActivityUtc", "activityUtc")).localeCompare(String(getValue(left, "ActivityUtc", "activityUtc"))));

  return getValue(activity[0], "ActivityUtc", "activityUtc") || getValue(resource, "CreatedDate", "createdDate");
};

const buildPurchasedOfferLabel = (resource) => {
  const offerKey = getString(resource, "ProductOfferKey", "productOfferKey");
  const offerSku = getString(resource, "ProductOfferSku", "productOfferSku");

  return [formatDisplayLabel(offerKey), offerSku].filter(Boolean).join(" - ") || "No offer recorded";
};

const ResourceApprovalsManage = () => {
  const { canView } = useCorePermissions();
  const clientResourceMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.ClientResource);
  const clientResourceMgrRef = useRef(clientResourceMgr);
  const canManageApprovals = canView("ProductMarketplaceClientResourceApproval") || canView("ManageAllClients");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    clientResourceMgrRef.current = clientResourceMgr;
  }, [clientResourceMgr]);

  const loadQueue = useCallback(async () => {
    if (!canManageApprovals) {
      setLoading(false);
      return;
    }

    const manager = clientResourceMgrRef.current;
    if (!manager?.RunOperation) {
      setError("Resource approval services are unavailable.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const result = await manager.RunOperation({
        operationName: Operations.ClientResourceItem.LoadClientResourceApprovalQueue,
        queryParams: { includeResolved: "false" },
        isWarning: true
      });
      const normalized = unwrapResult(result);
      if (normalized?.Success === false || normalized?.success === false) {
        throw new Error(getDisplayMessage(normalized, "Resource approval queue could not be loaded."));
      }
      setRows(getItems(normalized));
    } catch (ex) {
      setRows([]);
      setError(ex?.message || "Resource approval queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [canManageApprovals]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const runAction = useCallback(async (operationName, row) => {
    const manager = clientResourceMgrRef.current;
    const clientResourceId = getId(row);
    if (!manager?.RunOperation || !clientResourceId) return;

    setBusyId(clientResourceId);
    setError("");
    setStatusMessage("");
    try {
      const result = await manager.RunOperation({
        operationName,
        item: {
          _id: clientResourceId,
          ClientResourceId: clientResourceId
        },
        queryParams: { clientResourceId },
        isItem: true,
        isWarning: true
      });
      const normalized = unwrapResult(result);
      if (normalized?.Success === false || normalized?.success === false) {
        throw new Error(getDisplayMessage(normalized, "Resource approval action failed."));
      }
      setStatusMessage(getDisplayMessage(normalized, "Resource approval action complete."));
      await loadQueue();
    } catch (ex) {
      setError(ex?.message || "Resource approval action failed.");
    } finally {
      setBusyId("");
    }
  }, [loadQueue]);

  const pendingRows = useMemo(
    () => rows.filter((row) => getNumber(row, "LifecycleStatus", "lifecycleStatus") === 10),
    [rows]
  );

  if (!canManageApprovals) {
    return (
      <main className="resource-approvals-manage">
        <div className="resource-approvals-empty">
          <IconControl iconKey="warning" width="1.4rem" height="1.4rem" />
          <h1>Resource Approvals</h1>
          <p>You do not have access to Resource Approvals.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="resource-approvals-manage">
      <header className="resource-approvals-header">
        <div>
          <span>Administration</span>
          <h1>Resource Approvals</h1>
        </div>
        <ButtonControl type="standard" onClick={() => void loadQueue()} disabled={loading || !!busyId}>
          Refresh
        </ButtonControl>
      </header>

      {statusMessage ? <div className="resource-approvals-status">{statusMessage}</div> : null}
      {error ? <div className="resource-approvals-error">{error}</div> : null}

      {loading ? (
        <div className="resource-approvals-loading">
          <Loading text="Loading resource approvals..." />
        </div>
      ) : pendingRows.length === 0 ? (
        <div className="resource-approvals-empty">
          <IconControl iconKey="manage" width="1.4rem" height="1.4rem" />
          <h2>No pending resource approvals</h2>
          <p>Approval-required Client Resources will appear here when clients request them.</p>
        </div>
      ) : (
        <div className="resource-approvals-table-wrap">
          <table className="resource-approvals-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Resource</th>
                <th>Status</th>
                <th>Approval</th>
                <th>Purchased Offers</th>
                <th>Allowance</th>
                <th>Requested</th>
                <th>Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingRows.map((row) => {
                const id = getId(row);
                const allowance = getValue(row, "ResourceAllowance", "resourceAllowance") || {};
                const isBusy = busyId === id;

                return (
                  <tr key={id}>
                    <td>{getString(row, "ClientId", "clientId") || "Client not set"}</td>
                    <td>
                      <strong>{getString(row, "DisplayName", "displayName", "ResourceName", "resourceName")}</strong>
                      <small>{getString(row, "ProductName", "productName")} - {getString(row, "ProductResourceKey", "productResourceKey")}</small>
                    </td>
                    <td>{getStatusLabel(CLIENT_RESOURCE_STATUS_LABELS, getValue(row, "LifecycleStatus", "lifecycleStatus"))}</td>
                    <td>{getStatusLabel(APPROVAL_STATUS_LABELS, getValue(row, "ApprovalStatus", "approvalStatus"))}</td>
                    <td>{buildPurchasedOfferLabel(row)}</td>
                    <td>{formatCapacity(getValue(allowance, "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount"))}</td>
                    <td>{formatDateTime(getRequestedUtc(row)) || "Not recorded"}</td>
                    <td>{getString(row, "StatusReason", "statusReason") || "No reason recorded"}</td>
                    <td>
                      <div className="resource-approvals-actions">
                        <ButtonControl type="ok" disabled={isBusy} onClick={() => void runAction(Operations.ClientResourceItem.ApproveClientResource, row)}>Approve</ButtonControl>
                        <ButtonControl type="standard" disabled={isBusy} onClick={() => void runAction(Operations.ClientResourceItem.RejectClientResource, row)}>Reject</ButtonControl>
                        <ButtonControl type="standard" disabled={isBusy} onClick={() => void runAction(Operations.ClientResourceItem.SuspendClientResource, row)}>Suspend</ButtonControl>
                        <ButtonControl type="standard" disabled={isBusy} onClick={() => void runAction(Operations.ClientResourceItem.CancelClientResource, row)}>Cancel</ButtonControl>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default ResourceApprovalsManage;
